import React, { useState } from "react";
import {
  calculateBankingCost,
  exportBankingExcel,
} from "../../../../Rest_api/RestApi";
import {
  BankingCalculateResponse,
  BankingPeriodResult,
} from "../../../../models/banking";
import { Plus, Trash2, Copy, Download, Zap, ArrowRight, ArrowLeft } from "lucide-react";
import { PrimaryColor, SecondaryColor } from "./PriceAnalysisForm";
import { DynamicDatePicker } from "../../../../components/DatePicker";

// ── Row Interfaces ──
interface SupplyRow {
  id: string;
  startDate: string;
  endDate: string;
  mw: string;
  startTime: string;
  endTime: string;
}

interface ReturnRow {
  id: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  mw: string;
}

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

const generateTimeOptions = (): string[] => {
  const opts: string[] = [];
  for (let h = 0; h < 24; h++)
    for (let m = 0; m < 60; m += 15)
      opts.push(`${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`);
  opts.push("24:00");
  return opts;
};
const TIME_OPTIONS = generateTimeOptions();

function parseHours(st: string, et: string): number {
  const [sh, sm] = st.split(":").map(Number);
  const [eh, em] = et === "24:00" ? [24, 0] : et.split(":").map(Number);
  return (eh * 60 + em - sh * 60 - sm) / 60;
}

function formatCr(v: number): string {
  const cr = Math.abs(v) / 1e7;
  const s = v < 0 ? "-" : "";
  return cr >= 1 ? `${s}${cr.toFixed(2)} Cr` : `${s}${(Math.abs(v) / 1e5).toFixed(2)} L`;
}

function fmtN(v: number): string {
  return v.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

// ── Custom Table ──
const TH: React.FC<{ children: React.ReactNode; align?: "left" | "center" | "right" }> = ({ children, align = "left" }) => (
  <div
    className="px-3 py-2 text-[10px] font-medium text-gray-400 uppercase tracking-wider"
    style={{ textAlign: align }}
  >
    {children}
  </div>
);

const TD: React.FC<{ children: React.ReactNode; align?: "left" | "center" | "right"; color?: string }> = ({
  children,
  align = "left",
  color,
}) => (
  <div
    className="px-3 py-2 text-xs text-gray-600"
    style={{ textAlign: align, color }}
  >
    {children}
  </div>
);

// ── Component ──
const formatLocal = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const BankingCalculatorForm: React.FC = () => {
  const [supplyFrom, setSupplyFrom] = useState("HPPC");
  const [supplyTo, setSupplyTo] = useState("PCKL");
  const [supplyRows, setSupplyRows] = useState<SupplyRow[]>([
    { id: uid(), startDate: "", endDate: "", mw: "", startTime: "00:00", endTime: "24:00" }
  ]);
  const [returnPct, setReturnPct] = useState("104");
  const [returnQtyMode, setReturnQtyMode] = useState<"pct" | "mwh">("pct");
  const [returnDistMode, setReturnDistMode] = useState<"uniform" | "custom">("uniform");
  const [returnRows, setReturnRows] = useState<ReturnRow[]>([
    { id: uid(), startDate: "", endDate: "", startTime: "00:00", endTime: "24:00", mw: "" }
  ]);
  const [result, setResult] = useState<BankingCalculateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClear = () => {
    setSupplyFrom("HPPC");
    setSupplyTo("PCKL");
    setSupplyRows([
      { id: uid(), startDate: "", endDate: "", mw: "", startTime: "00:00", endTime: "24:00" }
    ]);
    setReturnPct("104");
    setReturnQtyMode("pct");
    setReturnDistMode("uniform");
    setReturnRows([{ id: uid(), startDate: "", endDate: "", startTime: "00:00", endTime: "24:00", mw: "" }]);
    setResult(null);
    setError(null);
  };

  const getSupplyEnergyMwh = (): number => {
    let total = 0;
    for (const r of supplyRows) {
      const mw = parseFloat(r.mw) || 0;
      if (!r.startDate || !r.endDate || !mw) continue;
      const days = (new Date(r.endDate).getTime() - new Date(r.startDate).getTime()) / 86400000 + 1;
      total += mw * parseHours(r.startTime || "00:00", r.endTime || "24:00") * days;
    }
    return total;
  };

  const getReturnTargetMwh = (): number => {
    const val = parseFloat(returnPct) || 0;
    return returnQtyMode === "pct" ? getSupplyEnergyMwh() * (val / 100) : val;
  };

  const getAllocatedMwh = (): number => {
    if (returnDistMode !== "custom") return 0;
    let total = 0;
    for (const r of returnRows) {
      const mw = parseFloat(r.mw) || 0;
      if (!r.startDate || !r.endDate) continue;
      const days = (new Date(r.endDate).getTime() - new Date(r.startDate).getTime()) / 86400000 + 1;
      total += mw * parseHours(r.startTime || "00:00", r.endTime || "24:00") * days;
    }
    return total;
  };

  const getReturnTotalHours = (): number => {
    let total = 0;
    for (const r of returnRows) {
      if (!r.startDate || !r.endDate) continue;
      const days = (new Date(r.endDate).getTime() - new Date(r.startDate).getTime()) / 86400000 + 1;
      if (days <= 0) continue;
      total += parseHours(r.startTime || "00:00", r.endTime || "24:00") * days;
    }
    return total;
  };

  const addSupplyRow = (sourceId?: string) => {
    const src = sourceId ? supplyRows.find((r) => r.id === sourceId) : null;
    setSupplyRows([...supplyRows, { id: uid(), startDate: src?.startDate || "", endDate: src?.endDate || "", mw: src?.mw || "", startTime: src?.startTime || "00:00", endTime: src?.endTime || "24:00" }]);
  };
  const removeSupplyRow = (id: string) => { if (supplyRows.length > 1) setSupplyRows(supplyRows.filter((r) => r.id !== id)); };
  const updateSupply = (id: string, field: keyof SupplyRow, value: string) => { setSupplyRows(supplyRows.map((r) => (r.id === id ? { ...r, [field]: value } : r))); };

  const addReturnRow = () => { setReturnRows([...returnRows, { id: uid(), startDate: "", endDate: "", startTime: "00:00", endTime: "24:00", mw: "" }]); };
  const removeReturnRow = (id: string) => { if (returnRows.length > 1) setReturnRows(returnRows.filter((r) => r.id !== id)); };
  const updateReturn = (id: string, field: keyof ReturnRow, value: string) => { setReturnRows(returnRows.map((r) => (r.id === id ? { ...r, [field]: value } : r))); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const supply_schedule = supplyRows.filter((r) => r.startDate && r.endDate && r.mw).map((r) => ({ start_date: r.startDate, end_date: r.endDate, quantum_mw: parseFloat(r.mw), start_time: r.startTime || "00:00", end_time: r.endTime || "24:00" }));
    const return_schedule = returnRows.filter((r) => r.startDate && r.endDate).map((r) => {
      const entry: any = { start_date: r.startDate, end_date: r.endDate, start_time: r.startTime || "00:00", end_time: r.endTime || "24:00" };
      if (returnDistMode === "custom" && r.mw) entry.quantum_mw = parseFloat(r.mw);
      return entry;
    });
    if (!supply_schedule.length) { setError("Add at least one supply period."); setLoading(false); return; }
    if (!return_schedule.length) { setError("Add at least one return period."); setLoading(false); return; }
    const supplyMwh = getSupplyEnergyMwh();
    const rawVal = parseFloat(returnPct) || 104;
    const pctForApi = returnQtyMode === "pct" ? rawVal : supplyMwh > 0 ? (rawVal / supplyMwh) * 100 : 104;
    try {
      const resp = await calculateBankingCost({ supply_from: supplyFrom, supply_to: supplyTo, supply_schedule, return_schedule, return_pct: pctForApi, return_mode: returnDistMode });
      setResult(resp);
    } catch (err: any) { setError(err?.message || "Calculation failed"); }
    setLoading(false);
  };

  const handleExport = async () => {
    if (!result) return;
    setExporting(true);
    try {
      const blob = await exportBankingExcel({ ...result, supply_from: supplyFrom, supply_to: supplyTo });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Banking_${supplyFrom}_${supplyTo}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setError("Export failed"); }
    setExporting(false);
  };

  const target = getReturnTargetMwh();
  const allocated = getAllocatedMwh();
  const barPct = target > 0 ? Math.min((allocated / target) * 100, 100) : 0;
  const trackerMatch = target > 0 && Math.abs(allocated - target) < target * 0.01;
  const trackerOver = allocated > target;

  const renderBadges = (p: BankingPeriodResult) => (
    <div className="flex gap-1 mt-0.5">
      {p.actual_days > 0 && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">Actual {p.actual_days}d</span>}
      {p.forecast_days > 0 && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100">Prev Yr {p.forecast_days}d</span>}
    </div>
  );

  const inp = "w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all duration-200 bg-white text-gray-700 text-sm";
  const inpSm = "w-full px-2 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all duration-200 bg-white text-gray-700 text-sm";
  const lbl = "block font-medium text-xs mb-1 text-gray-700";

  return (
    <div className="w-full">


      <form onSubmit={handleSubmit} className="bg-white rounded-xl p-5 border border-gray-100">
        {/* Parties Removed */}

        {/* Top Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Injection Card */}
          <div className="relative overflow-hidden rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-4 shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] transition-all hover:shadow-md hover:-translate-y-0.5">
            <div className="absolute -top-4 -right-4 h-16 w-16 rounded-full bg-indigo-100/60 blur-xl" />
            <div className="flex flex-col relative z-10">
              <span className="text-[10px] font-medium uppercase tracking-wider text-indigo-500 mb-1 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" /> Total Injection
              </span>
              <div className="flex items-end gap-1.5 mt-1">
                <span className="text-2xl font-medium text-indigo-900 leading-none tracking-tight">{fmtN(getSupplyEnergyMwh())}</span>
                <span className="text-xs font-medium text-indigo-600 mb-0.5">MWh</span>
              </div>
            </div>
          </div>
          
          {/* Return Required Card */}
          <div className="relative overflow-hidden rounded-xl border border-orange-100 bg-gradient-to-br from-orange-50 to-white p-4 shadow-[0_2px_10px_-3px_rgba(255,154,60,0.1)] transition-all hover:shadow-md hover:-translate-y-0.5">
            <div className="absolute -top-4 -right-4 h-16 w-16 rounded-full bg-orange-100/60 blur-xl" />
            <div className="flex flex-col relative z-10">
              <span className="flex justify-between items-center text-[10px] font-medium uppercase tracking-wider text-orange-600 mb-1">
                Return Required
                <span className="text-[9px] font-normal text-orange-400 capitalize normal-case tracking-normal">(as per Banking Factor)</span>
              </span>
              <div className="flex items-end gap-1.5 mt-1">
                <span className="text-2xl font-medium text-orange-900 leading-none tracking-tight">{fmtN(target)}</span>
                <span className="text-xs font-medium text-orange-600 mb-0.5">MWh</span>
              </div>
            </div>
          </div>

          {/* Return Remaining Card */}
          <div className={`relative overflow-hidden rounded-xl border transition-all hover:shadow-md hover:-translate-y-0.5 p-4 ${returnDistMode === "custom" && target - allocated < 0 ? "border-red-200 bg-gradient-to-br from-red-50 to-white shadow-[0_2px_10px_-3px_rgba(239,68,68,0.15)]" : returnDistMode === "custom" && target - allocated === 0 ? "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white shadow-[0_2px_10px_-3px_rgba(16,185,129,0.15)]" : "border-slate-200 bg-gradient-to-br from-slate-50 to-white shadow-[0_2px_10px_-3px_rgba(100,116,139,0.1)]"}`}>
            <div className={`absolute -top-4 -right-4 h-16 w-16 rounded-full blur-xl ${returnDistMode === "custom" && target - allocated < 0 ? "bg-red-200/50" : returnDistMode === "custom" && target - allocated === 0 ? "bg-emerald-200/50" : "bg-slate-200/50"}`} />
            <div className="flex flex-col relative z-10">
              <span className={`text-[10px] font-medium uppercase tracking-wider mb-1 ${returnDistMode === "custom" && target - allocated < 0 ? "text-red-600" : returnDistMode === "custom" && target - allocated === 0 ? "text-emerald-600" : "text-slate-500"}`}>
                Return Remaining <i className="fas fa-info-circle ml-1 opacity-60" title="Target minus allocated" />
              </span>
              <div className="flex items-end gap-1.5 mt-1">
                <span className={`text-2xl font-medium leading-none tracking-tight ${returnDistMode === "custom" && target - allocated < 0 ? "text-red-700" : returnDistMode === "custom" && target - allocated === 0 ? "text-emerald-700" : "text-slate-700"}`}>
                  {returnDistMode === "custom" ? fmtN(target - allocated) : "0"}
                </span>
                <span className={`text-xs font-medium mb-0.5 ${returnDistMode === "custom" && target - allocated < 0 ? "text-red-600" : returnDistMode === "custom" && target - allocated === 0 ? "text-emerald-600" : "text-slate-500"}`}>MWh</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Supply + Return side by side ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          {/* Supply */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: PrimaryColor }}><ArrowRight className="w-3 h-3 text-white" /></div>
                <span className="font-medium text-sm text-gray-700">Injection/Drawal Schedule</span>
              </div>
              <button type="button" onClick={() => addSupplyRow()} className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-lg transition-all" style={{ color: PrimaryColor, background: "#fff7ed" }}>
                <Plus className="w-3 h-3" />Add
              </button>
            </div>
            <div className="space-y-2">
              {supplyRows.map((row) => (
                <div key={row.id} className="bg-gradient-to-br from-gray-50 to-white p-3 rounded-lg border border-gray-200">
                  <div className="flex flex-row items-end gap-2 flex-wrap">
                    <div className="flex-1 min-w-[250px]">
                      <label className={lbl}>Date Range <span className="text-red-500">*</span></label>
                      <DynamicDatePicker
                        setDate={(dates) => {
                          if (dates && dates.length > 0) {
                            const st = formatLocal(dates[0].startDate);
                            const et = formatLocal(dates[0].endDate);
                            setSupplyRows(prev => prev.map(r => r.id === row.id ? { ...r, startDate: st, endDate: et } : r));
                          }
                        }}
                        defaultDates={row.startDate ? { startDate: new Date(row.startDate), endDate: row.endDate ? new Date(row.endDate) : new Date(row.startDate), key: "selection" } : undefined}
                        buttonClassName={inpSm + " text-left cursor-pointer"}
                      />
                    </div>
                    <div className="w-[70px]">
                      <label className={lbl}>MW <span className="text-red-500">*</span></label>
                      <input type="number" value={row.mw} onChange={(e) => updateSupply(row.id, "mw", e.target.value)} className={inpSm} min="1" />
                    </div>
                    <div className="flex-1 min-w-[80px]">
                      <label className={lbl}>From Time</label>
                      <select value={row.startTime} onChange={(e) => updateSupply(row.id, "startTime", e.target.value)} className={inpSm}>
                        {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="flex-1 min-w-[80px]">
                      <label className={lbl}>To Time</label>
                      <select value={row.endTime} onChange={(e) => updateSupply(row.id, "endTime", e.target.value)} className={inpSm}>
                        {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="flex gap-1 items-end">
                      <button type="button" onClick={() => addSupplyRow(row.id)} className="w-7 h-7 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-all" title="Duplicate"><Copy className="w-3 h-3" /></button>
                      {supplyRows.length > 1 && <button type="button" onClick={() => removeSupplyRow(row.id)} className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-all" title="Remove"><Trash2 className="w-3 h-3" /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Return */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: SecondaryColor }}><ArrowLeft className="w-3 h-3 text-white" /></div>
                <span className="font-medium text-sm text-gray-700">Return Schedule</span>
              </div>
              <button type="button" onClick={addReturnRow} className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-lg transition-all" style={{ color: SecondaryColor, background: "#f0fdfa" }}>
                <Plus className="w-3 h-3" />Add
              </button>
            </div>

            {/* Config */}
            <div className="bg-gradient-to-br from-teal-50 to-white p-3 rounded-lg border border-teal-200 mb-2 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-gray-500">Quantum</span>
                <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
                  {(["pct", "mwh"] as const).map((m) => (
                    <button key={m} type="button" onClick={() => setReturnQtyMode(m)} className={`px-2.5 py-0.5 rounded-md text-[11px] font-medium transition-all ${returnQtyMode === m ? "bg-white text-gray-800 shadow-sm" : "text-gray-400"}`}>
                      {m === "pct" ? "%" : "MWh"}
                    </button>
                  ))}
                </div>
                <input type="number" value={returnPct} onChange={(e) => setReturnPct(e.target.value)} className="w-20 px-2 py-1 border-2 border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100" step={returnQtyMode === "pct" ? "0.5" : "100"} />
                <span className="text-xs text-gray-400">{returnQtyMode === "pct" ? "%" : "MWh"}</span>
                {returnQtyMode === "pct" && <span className="text-[10px] text-gray-400 italic">= {fmtN(target)} MWh</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500">Distribution</span>
                <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
                  {(["uniform", "custom"] as const).map((m) => (
                    <button key={m} type="button" onClick={() => setReturnDistMode(m)} className={`px-2.5 py-0.5 rounded-md text-[11px] font-medium transition-all ${returnDistMode === m ? "bg-white text-gray-800 shadow-sm" : "text-gray-400"}`}>
                      {m === "uniform" ? "Uniform" : "Custom MW"}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Rows */}
            <div className="space-y-2">
              {returnRows.map((row) => (
                <div key={row.id} className="bg-gradient-to-br from-gray-50 to-white p-3 rounded-lg border border-gray-200">
                  <div className="flex flex-row items-end gap-2 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <label className={lbl}>Date Range <span className="text-red-500">*</span></label>
                      <DynamicDatePicker
                        setDate={(dates) => {
                          if (dates && dates.length > 0) {
                            const st = formatLocal(dates[0].startDate);
                            const et = formatLocal(dates[0].endDate);
                            setReturnRows(prev => prev.map(r => r.id === row.id ? { ...r, startDate: st, endDate: et } : r));
                          }
                        }}
                        defaultDates={row.startDate ? { startDate: new Date(row.startDate), endDate: row.endDate ? new Date(row.endDate) : new Date(row.startDate), key: "selection" } : undefined}
                        buttonClassName={inpSm + " text-left cursor-pointer"}
                      />
                    </div>
                    <div className="flex-1 min-w-[70px]">
                      <label className={lbl}>From Time</label>
                      <select value={row.startTime} onChange={(e) => updateReturn(row.id, "startTime", e.target.value)} className={inpSm}>
                        {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="flex-1 min-w-[70px]">
                      <label className={lbl}>To Time</label>
                      <select value={row.endTime} onChange={(e) => updateReturn(row.id, "endTime", e.target.value)} className={inpSm}>
                        {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    {returnDistMode === "custom" ? (
                      <div className="w-[70px]">
                        <label className={lbl}>MW</label>
                        <input type="number" value={row.mw} onChange={(e) => updateReturn(row.id, "mw", e.target.value)} className={inpSm} min="1" />
                      </div>
                    ) : (
                      <div className="w-[70px]">
                        <label className="block font-medium text-xs mb-1 text-gray-400 opacity-70">MW</label>
                        <input 
                          type="text" 
                          disabled 
                          value={row.startDate && row.endDate && getReturnTotalHours() > 0 ? (getReturnTargetMwh() / getReturnTotalHours()).toFixed(2) : ""} 
                          className="w-full px-2 py-2 border-2 border-gray-200 rounded-lg bg-gray-100 text-gray-400 text-sm cursor-not-allowed opacity-70" 
                          title="Uniformly distributed allocation"
                        />
                      </div>
                    )}
                    <div className="flex gap-1 items-end">
                      {returnRows.length > 1 && <button type="button" onClick={() => removeReturnRow(row.id)} className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-all" title="Remove"><Trash2 className="w-3 h-3" /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-center gap-3 pt-3">
          <button type="button" onClick={handleClear} className="px-6 py-2 rounded-lg text-gray-700 font-medium text-sm border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 transform hover:scale-105">
            <i className="fas fa-redo mr-1.5 text-xs" />Clear
          </button>
          <button type="submit" disabled={loading} className="px-6 py-2 rounded-lg text-white font-medium text-sm transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: loading ? "#ccc" : `linear-gradient(135deg, ${PrimaryColor}, #ff9a3c)` }}>
            {loading ? <><i className="fas fa-spinner fa-spin mr-1.5 text-xs" />Processing...</> : <><Zap className="w-4 h-4 inline mr-1.5" />Calculate</>}
          </button>
        </div>

        {/* Footnote */}
        <div className="mt-4 text-center text-[11px] text-gray-400 italic">
          * Note: All costs are calculated considering IEX DAM prices.
        </div>
      </form>

      {error && (
        <div className="mt-3 bg-red-50 border-l-4 border-red-500 p-3 rounded-lg">
          <div className="flex items-center"><i className="fas fa-exclamation-circle text-red-500 mr-2 text-sm" /><p className="text-red-700 font-medium text-sm">{error}</p></div>
        </div>
      )}

      {/* ── Results ── */}
      {result && (
        <div className="mt-3 bg-white rounded-xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
            <h2 className="text-base font-medium text-gray-800">Banking Analysis</h2>
            <button type="button" onClick={handleExport} disabled={exporting} className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium border border-green-200 text-green-700 hover:bg-green-50 transition-all disabled:opacity-50">
              <Download className="w-4 h-4" />{exporting ? "Exporting..." : "Export Excel"}
            </button>
          </div>

          {/* Summary Cards */}
          <div className="flex flex-row gap-3 mb-4">
            <div className="flex flex-col items-center bg-gradient-to-br from-white to-indigo-50 p-4 rounded-lg hover:scale-105 transition-all duration-300 flex-1 border border-indigo-100">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center mb-2"><ArrowRight className="w-5 h-5 text-white" /></div>
              <h3 className="text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Supply Cost</h3>
              <p className="text-xl font-medium text-gray-800 mb-0.5">{formatCr(result.supply.total_cost)}</p>
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">{fmtN(result.supply.total_energy_mwh)} MWh</span>
            </div>
            <div className="flex flex-col items-center bg-gradient-to-br from-white to-emerald-50 p-4 rounded-lg hover:scale-105 transition-all duration-300 flex-1 border border-emerald-100">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mb-2"><ArrowLeft className="w-5 h-5 text-white" /></div>
              <h3 className="text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Return Value</h3>
              <p className="text-xl font-medium text-gray-800 mb-0.5">{formatCr(result.return.total_cost)}</p>
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">{fmtN(result.return.total_energy_mwh)} MWh</span>
            </div>
            <div className={`flex flex-col items-center bg-gradient-to-br ${result.verdict === "PROFITABLE" ? "from-white to-emerald-50 border-emerald-200" : "from-white to-red-50 border-red-200"} p-4 rounded-lg hover:scale-105 transition-all duration-300 flex-1 border-2`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${result.verdict === "PROFITABLE" ? "bg-gradient-to-br from-emerald-400 to-emerald-600" : "bg-gradient-to-br from-red-400 to-red-600"}`}><Zap className="w-5 h-5 text-white" /></div>
              <h3 className="text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Net Savings</h3>
              <p className={`text-xl font-medium mb-0.5 ${result.verdict === "PROFITABLE" ? "text-emerald-600" : "text-red-600"}`}>{formatCr(Math.abs(result.net_savings))}</p>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${result.verdict === "PROFITABLE" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{result.verdict}</span>
            </div>
          </div>

          {/* ── Tables side by side ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Supply table */}
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-100 flex items-center gap-2">
                <ArrowRight className="w-3 h-3 text-indigo-500" />
                <span className="text-xs font-medium text-gray-700">Supply Breakdown</span>
              </div>
              {/* Header */}
              <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-100">
                <div className="col-span-4"><TH>Period</TH></div>
                <div className="col-span-2"><TH align="center">Time</TH></div>
                <div className="col-span-1"><TH align="right">MW</TH></div>
                <div className="col-span-2"><TH align="right">MWh</TH></div>
                <div className="col-span-3"><TH align="right">Cost</TH></div>
              </div>
              {/* Rows */}
              {result.supply.periods.map((p, i) => (
                <div key={i} className={`grid grid-cols-12 border-b border-gray-50 hover:bg-gray-50/50 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/30"}`}>
                  <div className="col-span-4"><TD><span className="text-gray-700">{p.period}</span>{renderBadges(p)}</TD></div>
                  <div className="col-span-2"><TD align="center">{p.time_range}</TD></div>
                  <div className="col-span-1"><TD align="right">{fmtN(p.quantum_mw!)}</TD></div>
                  <div className="col-span-2"><TD align="right">{fmtN(p.energy_mwh)}</TD></div>
                  <div className="col-span-3"><TD align="right" color="#4338ca">{formatCr(p.cost)}</TD></div>
                </div>
              ))}
              {/* Total */}
              <div className="grid grid-cols-12 bg-indigo-50/60">
                <div className="col-span-4"><TD><span className="text-gray-700">Total</span></TD></div>
                <div className="col-span-2"><TD align="center">-</TD></div>
                <div className="col-span-1"><TD align="right">-</TD></div>
                <div className="col-span-2"><TD align="right">{fmtN(result.supply.total_energy_mwh)}</TD></div>
                <div className="col-span-3"><TD align="right" color="#4338ca">{formatCr(result.supply.total_cost)}</TD></div>
              </div>
            </div>

            {/* Return table */}
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
                <ArrowLeft className="w-3 h-3 text-emerald-500" />
                <span className="text-xs font-medium text-gray-700">Return Breakdown</span>
                <span className="text-[10px] text-gray-400 ml-auto">{result.return.return_pct}% | {result.return.return_mode}</span>
              </div>
              {/* Header */}
              <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-100">
                <div className="col-span-4"><TH>Period</TH></div>
                <div className="col-span-2"><TH align="center">Time</TH></div>
                <div className="col-span-1"><TH align="right">MW</TH></div>
                <div className="col-span-2"><TH align="right">MWh</TH></div>
                <div className="col-span-3"><TH align="right">Value</TH></div>
              </div>
              {/* Rows */}
              {result.return.periods.map((p, i) => (
                <div key={i} className={`grid grid-cols-12 border-b border-gray-50 hover:bg-gray-50/50 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/30"}`}>
                  <div className="col-span-4"><TD><span className="text-gray-700">{p.period}</span>{renderBadges(p)}</TD></div>
                  <div className="col-span-2"><TD align="center">{p.time_range}</TD></div>
                  <div className="col-span-1"><TD align="right">{fmtN(p.return_mw!)}</TD></div>
                  <div className="col-span-2"><TD align="right">{fmtN(p.energy_mwh)}</TD></div>
                  <div className="col-span-3"><TD align="right" color="#047857">{formatCr(p.cost)}</TD></div>
                </div>
              ))}
              {/* Total */}
              <div className="grid grid-cols-12 bg-emerald-50/60">
                <div className="col-span-4"><TD><span className="text-gray-700">Total</span></TD></div>
                <div className="col-span-2"><TD align="center">-</TD></div>
                <div className="col-span-1"><TD align="right">-</TD></div>
                <div className="col-span-2"><TD align="right">{fmtN(result.return.total_energy_mwh)}</TD></div>
                <div className="col-span-3"><TD align="right" color="#047857">{formatCr(result.return.total_cost)}</TD></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankingCalculatorForm;
